import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {IMeshGeometryQueries} from "#template/interfaces/scene-manager"
import type {Matrix4} from "@cm/math"
import {cameraAimDirection, raycastCameraDistance} from "#template/utils/camera-utils"
import type {Subscription} from "rxjs"

const TD = TypeDescriptors

const raycastFocalDistanceDescriptor = {
    meshGeometryQueries: TD.inlet(TD.Identity<IMeshGeometryQueries>()),
    transform: TD.inlet(TD.Matrix4),
    autoFocus: TD.inlet(TD.Boolean),
    fallbackFocalDistance: TD.inlet(TD.Number),
    focalDistance: TD.outlet(TD.Number),
}

export class RaycastFocalDistance implements NodeClassImpl<typeof raycastFocalDistanceDescriptor, typeof RaycastFocalDistance> {
    static descriptor = raycastFocalDistanceDescriptor
    static uniqueName = "RaycastFocalDistance"
    meshGeometryQueries!: Inlet<IMeshGeometryQueries>
    transform!: Inlet<Matrix4>
    autoFocus!: Inlet<boolean>
    fallbackFocalDistance!: Inlet<number>
    focalDistance!: Outlet<number>

    private subscription: Subscription | null = null

    run() {
        this.cleanup()

        if (this.meshGeometryQueries === NotReady || this.transform === NotReady || this.autoFocus === NotReady || this.fallbackFocalDistance === NotReady) {
            this.focalDistance.emitIfChanged(NotReady)
            return
        }

        const {meshGeometryQueries, transform, autoFocus, fallbackFocalDistance} = this

        if (!autoFocus) {
            this.focalDistance.emitIfChanged(fallbackFocalDistance)
            return
        }

        const raycast = () => {
            const origin = transform.getTranslation()
            const aimDirection = cameraAimDirection(transform)
            this.focalDistance.emitIfChanged(raycastCameraDistance(meshGeometryQueries, origin, aimDirection, fallbackFocalDistance))
        }

        raycast()
        this.subscription = meshGeometryQueries.sceneGeometryChanged().subscribe(raycast)
    }

    cleanup() {
        this.subscription?.unsubscribe()
        this.subscription = null
    }
}
